/**
 * VerDFDPage — visualização do DFD gerado (F5e).
 *
 * Réplica do view_dfd_v2.html: cabeçalho, botões de ação, cards da cadeia
 * (DFD/ETP/TR/Próximos), sumário com âncoras, corpo do documento com o
 * bloco de identificação, o card de modalidade (Art. 28), as 5 seções e as
 * referências.
 *
 * As seções chegam do backend já em HTML — convertidas pelo MESMO filtro
 * markdown_to_html que o Jinja usa, para as duas telas renderizarem igual
 * enquanto coexistem.
 */

import {
  faArrowLeft,
  faBalanceScale,
  faBook,
  faBox,
  faCalendar,
  faCalendarCheck,
  faCheck,
  faCheckCircle,
  faChevronRight,
  faClipboardList,
  faEdit,
  faExclamationTriangle,
  faEye,
  faFileAlt,
  faFileContract,
  faFileInvoice,
  faFileWord,
  faGavel,
  faInfoCircle,
  faListOl,
  faPlus,
  faScroll,
  faShieldAlt,
  faSpinner,
  faTrashAlt,
  type IconDefinition,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router'
import { documentoDFD, excluirDFD, type DocumentoDFD } from '../../api/dfd'
import { useToast } from '../../components/Toast'

function Identificacao({ dfd }: { dfd: DocumentoDFD }) {
  const linhas: [string, string][] = [
    ['PROCESSO SEI Nº', dfd.numero_processo || '[NÚMERO DO PROCESSO]'],
    ['MUNICÍPIO', dfd.municipio || 'N/A'],
    ['OBJETO', dfd.objeto || 'N/A'],
    ['ÓRGÃO SOLICITANTE', dfd.orgao_solicitante || 'N/A'],
    ['SERVIDOR RESPONSÁVEL', dfd.servidor_responsavel || 'N/A'],
  ]
  if (dfd.cpf_servidor) linhas.push(['CPF', dfd.cpf_servidor])
  if (dfd.matricula_servidor) linhas.push(['MATRÍCULA', dfd.matricula_servidor])
  linhas.push(['DATA DE ELABORAÇÃO', dfd.data_elaboracao || 'N/A'])

  return (
    <div className="mb-8 border-b-2 border-green-600 pb-6 text-center">
      <h2 className="mb-4 text-2xl font-bold text-slate-800 dark:text-slate-100">
        DOCUMENTO DE FORMALIZAÇÃO DA DEMANDA - DFD
      </h2>
      <dl className="space-y-2 text-slate-600 dark:text-slate-300">
        {linhas.map(([rotulo, valor]) => (
          <div key={rotulo}>
            <dt className="inline font-bold">{rotulo}:</dt>{' '}
            <dd className="inline">{valor}</dd>
          </div>
        ))}
      </dl>
    </div>
  )
}

function CardModalidade({ dfd }: { dfd: DocumentoDFD }) {
  const m = dfd.modalidade
  if (!m) return null
  return (
    <div className="mb-6 rounded-lg border border-indigo-200 bg-gradient-to-r from-indigo-50 to-purple-50 p-6 dark:border-indigo-800 dark:from-indigo-900/20 dark:to-purple-900/20">
      <h3 className="mb-4 text-lg font-semibold text-indigo-800 dark:text-indigo-300">
        <FontAwesomeIcon icon={faGavel} className="mr-2 text-indigo-600" />
        Modalidade de Licitação (Art. 28, Lei 14.133/2021)
      </h3>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <div className="rounded-lg border border-indigo-100 bg-white p-4 dark:border-indigo-900 dark:bg-dark-card">
          <p className="mb-2 flex items-center text-sm font-medium text-slate-500">
            <span className="mr-3 flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600">
              <FontAwesomeIcon icon={faBox} className="text-white" />
            </span>
            Tipo de Objeto
          </p>
          <p className="text-lg font-semibold text-slate-800 dark:text-slate-100">
            {m.tipo_objeto_rotulo}
          </p>
        </div>

        <div className="rounded-lg border border-purple-100 bg-white p-4 dark:border-purple-900 dark:bg-dark-card">
          <p className="mb-2 flex items-center text-sm font-medium text-slate-500">
            <span className="mr-3 flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-purple-500 to-pink-600">
              <FontAwesomeIcon icon={faBalanceScale} className="text-white" />
            </span>
            Modalidade
          </p>
          <p className="text-lg font-semibold text-slate-800 dark:text-slate-100">
            {m.modalidade_rotulo}
          </p>
          <span
            className={`mt-2 inline-flex items-center rounded-full px-2 py-0.5 text-xs ${
              m.obrigatoria
                ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300'
                : 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300'
            }`}
          >
            <FontAwesomeIcon
              icon={m.obrigatoria ? faCheckCircle : faInfoCircle}
              className="mr-1"
            />
            {m.obrigatoria ? 'Modalidade Obrigatória' : 'Modalidade Alternativa'}
          </span>
        </div>

        <div className="rounded-lg border border-emerald-100 bg-white p-4 dark:border-emerald-900 dark:bg-dark-card">
          <p className="mb-2 flex items-center text-sm font-medium text-slate-500">
            <span className="mr-3 flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600">
              <FontAwesomeIcon icon={faClipboardList} className="text-white" />
            </span>
            Regime de Execução
          </p>
          {m.registro_precos ? (
            <>
              <span className="inline-flex items-center rounded-full bg-emerald-100 px-2.5 py-1 text-sm text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300">
                <FontAwesomeIcon icon={faFileInvoice} className="mr-1" />
                SRP/ARP
              </span>
              <p className="mt-1 text-xs text-slate-500">
                Sistema de Registro de Preços (Art. 82-86)
              </p>
            </>
          ) : (
            <p className="text-lg font-semibold text-slate-800 dark:text-slate-100">
              Contratação Direta
            </p>
          )}
        </div>
      </div>

      {m.justificativa && (
        <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-900/20">
          <h4 className="mb-2 text-sm font-semibold text-amber-800 dark:text-amber-300">
            <FontAwesomeIcon icon={faExclamationTriangle} className="mr-1" />
            Justificativa para Modalidade Alternativa
          </h4>
          <p className="text-sm text-amber-700 dark:text-amber-400">{m.justificativa}</p>
        </div>
      )}
    </div>
  )
}

function CardCadeia({
  titulo,
  descricao,
  icone,
  gradiente,
  estado,
  verUrl,
  editarUrl,
  criarUrl,
}: {
  titulo: string
  descricao: string
  icone: IconDefinition
  gradiente: string
  estado: 'atual' | 'pronto' | 'pendente'
  verUrl?: string
  editarUrl?: string
  criarUrl?: string
}) {
  return (
    <div className="group relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-5 shadow-lg transition-all duration-300 hover:-translate-y-1 hover:shadow-xl dark:border-slate-700 dark:bg-dark-card">
      <div className="relative z-10">
        <div className="mb-4 flex items-start justify-between">
          <span
            className={`flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br shadow-lg transition-transform duration-300 group-hover:scale-110 ${gradiente}`}
          >
            <FontAwesomeIcon icon={icone} className="text-lg text-white" />
          </span>
          <span
            className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
              estado === 'atual'
                ? 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300'
                : estado === 'pronto'
                  ? 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/50 dark:text-indigo-300'
                  : 'bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-400'
            }`}
          >
            {estado !== 'pendente' && <FontAwesomeIcon icon={faCheck} className="mr-1" />}
            {estado === 'atual' ? 'Visualizando' : estado === 'pronto' ? 'Criado' : 'Pendente'}
          </span>
        </div>
        <h3 className="mb-1 text-lg font-bold text-slate-800 dark:text-white">{titulo}</h3>
        <p className="mb-5 min-h-[2.5em] text-xs text-slate-500 dark:text-slate-400">
          {descricao}
        </p>
        <div className="flex gap-2">
          {verUrl && (
            <a
              href={verUrl}
              className="flex flex-1 items-center justify-center rounded-lg border border-green-200 bg-white py-2 text-sm font-semibold text-green-700 shadow-sm transition-all hover:bg-green-600 hover:text-white dark:border-green-800 dark:bg-slate-700 dark:text-green-400"
            >
              <FontAwesomeIcon icon={faEye} className="mr-2" />
              Ver
            </a>
          )}
          {editarUrl && (
            <a
              href={editarUrl}
              aria-label={`Editar ${titulo}`}
              className="flex w-10 items-center justify-center rounded-lg border border-emerald-200 bg-white py-2 text-emerald-700 shadow-sm transition-all hover:bg-emerald-600 hover:text-white dark:border-emerald-800 dark:bg-slate-700 dark:text-emerald-400"
            >
              <FontAwesomeIcon icon={faEdit} />
            </a>
          )}
          {criarUrl && (
            <a
              href={criarUrl}
              className="flex flex-1 items-center justify-center rounded-lg border border-slate-200 bg-white py-2 text-sm font-semibold text-slate-600 shadow-sm transition-all hover:bg-slate-600 hover:text-white dark:border-slate-600 dark:bg-slate-700 dark:text-slate-300"
            >
              <FontAwesomeIcon icon={faPlus} className="mr-2" />
              Criar
            </a>
          )}
        </div>
      </div>
    </div>
  )
}

function ModalExcluir({
  dfd,
  aoFechar,
}: {
  dfd: DocumentoDFD
  aoFechar: () => void
}) {
  const navegar = useNavigate()
  const clienteQuery = useQueryClient()
  const { notificar } = useToast()

  const excluir = useMutation({
    mutationFn: () => excluirDFD(dfd.id),
    onSuccess: (dados) => {
      void clienteQuery.invalidateQueries({ queryKey: ['processos-licitatorios'] })
      notificar(
        'success',
        dados.processo_removido
          ? 'Processo licitatório excluído.'
          : 'DFD excluído. O processo foi mantido porque tem outros documentos.',
      )
      navegar('/processos')
    },
    onError: (e: Error) => notificar('error', e.message),
  })

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label="Excluir DFD"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) aoFechar()
      }}
    >
      <div className="mx-4 w-full max-w-md rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-2xl dark:border-slate-700 dark:bg-dark-card">
        <span className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-red-500 to-orange-600 shadow-lg shadow-red-500/30">
          <FontAwesomeIcon icon={faTrashAlt} className="text-2xl text-white" />
        </span>
        <h3 className="mb-2 text-xl font-bold text-slate-800 dark:text-slate-100">
          Excluir DFD?
        </h3>
        <p className="mb-3 text-slate-600 dark:text-slate-400">
          Tem certeza que deseja excluir permanentemente este DFD?
        </p>
        <p className="mb-4 rounded-lg bg-slate-100 px-4 py-2 text-sm font-semibold text-slate-800 dark:bg-slate-700 dark:text-slate-200">
          {dfd.objeto}
        </p>
        <div className="mb-4 rounded-lg bg-red-50 p-4 text-left dark:bg-red-900/20">
          <p className="mb-2 text-sm font-medium text-red-700 dark:text-red-400">
            Esta ação irá:
          </p>
          <ul className="list-inside list-disc space-y-1 text-sm text-red-600 dark:text-red-400">
            <li>Remover todo o conteúdo do DFD</li>
            {dfd.cadeia.tem_etp || dfd.cadeia.tem_tr ? (
              <li>Manter o processo, que já tem outros documentos</li>
            ) : (
              <li>Excluir o processo licitatório inteiro</li>
            )}
          </ul>
        </div>
        <p className="mb-6 text-sm font-semibold text-red-600 dark:text-red-400">
          <FontAwesomeIcon icon={faExclamationTriangle} className="mr-1" />
          Esta ação não pode ser desfeita!
        </p>
        <div className="flex justify-center gap-3">
          <button
            type="button"
            onClick={aoFechar}
            className="rounded-xl bg-slate-200 px-6 py-3 font-medium text-slate-700 transition-all hover:bg-slate-300 dark:bg-slate-700 dark:text-slate-300"
          >
            <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
            Cancelar
          </button>
          <button
            type="button"
            onClick={() => excluir.mutate()}
            disabled={excluir.isPending}
            className="rounded-xl bg-gradient-to-r from-red-500 to-orange-600 px-6 py-3 font-semibold text-white shadow-lg shadow-red-500/30 transition-all hover:from-red-600 hover:to-orange-700 disabled:opacity-60"
          >
            <FontAwesomeIcon
              icon={excluir.isPending ? faSpinner : faTrashAlt}
              spin={excluir.isPending}
              className="mr-2"
            />
            Sim, Excluir
          </button>
        </div>
      </div>
    </div>
  )
}

export function VerDFDPage() {
  const { id } = useParams()
  const idNumerico = Number(id)
  const [excluindo, setExcluindo] = useState(false)

  const { data: dfd, isLoading, error } = useQuery({
    queryKey: ['dfd-documento', idNumerico],
    queryFn: () => documentoDFD(idNumerico),
    enabled: Number.isFinite(idNumerico),
    retry: false,
  })

  if (isLoading) {
    return (
      <p className="mt-12 text-center text-slate-500">
        <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
        Carregando DFD…
      </p>
    )
  }
  if (error || !dfd) {
    return (
      <div className="mx-auto mt-12 max-w-xl rounded-2xl bg-white p-8 text-center shadow-lg dark:bg-dark-card">
        <FontAwesomeIcon icon={faFileAlt} className="mb-4 text-5xl text-slate-300" />
        <h1 className="mb-2 text-2xl font-semibold text-slate-700 dark:text-slate-200">
          DFD não encontrado
        </h1>
        <p className="mb-6 text-slate-500">
          {(error as Error)?.message ?? 'O documento não existe ou não é seu.'}
        </p>
        <Link
          to="/processos"
          className="inline-flex items-center rounded-lg bg-green-600 px-6 py-3 font-semibold text-white hover:bg-green-700"
        >
          <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
          Voltar
        </Link>
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-7xl">
      <header className="mb-8 text-center">
        <h1 className="mb-2 text-4xl font-bold text-slate-800 dark:text-slate-100">
          <FontAwesomeIcon icon={faFileAlt} className="mr-3 text-green-600" />
          Documento de Formalização da Demanda (DFD)
        </h1>
        <p className="text-lg text-slate-600 dark:text-slate-400">Lei 14.133/2021</p>
        <div className="mt-4 flex flex-wrap justify-center gap-4">
          <span className="inline-flex items-center rounded-full bg-green-100 px-4 py-2 text-sm font-medium text-green-800 dark:bg-green-900/40 dark:text-green-300">
            <FontAwesomeIcon icon={faInfoCircle} className="mr-2" />
            ID: {dfd.id}
          </span>
          {dfd.criado_em && (
            <span className="inline-flex items-center rounded-full bg-indigo-100 px-4 py-2 text-sm font-medium text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-300">
              <FontAwesomeIcon icon={faCalendar} className="mr-2" />
              Criado em: {dfd.criado_em.slice(0, 10).split('-').reverse().join('/')}
            </span>
          )}
        </div>
      </header>

      <div className="mb-8 flex flex-col justify-center gap-4 sm:flex-row">
        <Link
          to={`/dfd/${dfd.id}/editar`}
          className="inline-flex items-center justify-center rounded-lg bg-blue-600 px-6 py-3 font-semibold text-white shadow-sm transition-all hover:bg-blue-700"
        >
          <FontAwesomeIcon icon={faEdit} className="mr-2" />
          Editar
        </Link>
        <a
          href={`/dfd/${dfd.id}/export/docx`}
          className="inline-flex items-center justify-center rounded-lg bg-emerald-600 px-6 py-3 font-semibold text-white shadow-sm transition-all hover:bg-emerald-700"
        >
          <FontAwesomeIcon icon={faFileWord} className="mr-2" />
          Exportar DOCX
        </a>
        <button
          type="button"
          onClick={() => setExcluindo(true)}
          className="inline-flex items-center justify-center rounded-lg bg-red-600 px-6 py-3 font-semibold text-white shadow-sm transition-all hover:bg-red-700"
        >
          <FontAwesomeIcon icon={faTrashAlt} className="mr-2" />
          Excluir
        </button>
        <Link
          to="/processos"
          className="inline-flex items-center justify-center rounded-lg bg-slate-600 px-6 py-3 font-semibold text-white shadow-sm transition-all hover:bg-slate-700"
        >
          <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
          Voltar
        </Link>
      </div>

      <div className="mb-8 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
        <CardCadeia
          titulo="DFD"
          descricao="Documento de Formalização da Demanda"
          icone={faFileAlt}
          gradiente="from-green-500 to-emerald-600"
          estado="atual"
          editarUrl={`/app/dfd/${dfd.id}/editar`}
        />
        <CardCadeia
          titulo="ETP"
          descricao="Estudo Técnico Preliminar"
          icone={faClipboardList}
          gradiente="from-indigo-500 to-purple-600"
          estado={dfd.cadeia.tem_etp ? 'pronto' : 'pendente'}
          verUrl={dfd.cadeia.tem_etp ? `/app/etp/${dfd.id}` : undefined}
          editarUrl={dfd.cadeia.tem_etp ? `/app/etp/${dfd.id}/editar` : undefined}
          criarUrl={dfd.cadeia.tem_etp ? undefined : `/app/etp/novo/${dfd.id}`}
        />
        <CardCadeia
          titulo="TR"
          descricao="Termo de Referência"
          icone={faFileContract}
          gradiente="from-blue-500 to-cyan-600"
          estado={dfd.cadeia.tem_tr ? 'pronto' : 'pendente'}
          verUrl={dfd.cadeia.tem_tr ? `/app/tr/${dfd.id}` : undefined}
          editarUrl={dfd.cadeia.tem_tr ? `/app/tr/${dfd.id}/editar` : undefined}
          criarUrl={
            !dfd.cadeia.tem_tr && dfd.cadeia.tem_etp ? `/app/tr/novo/${dfd.id}` : undefined
          }
        />
        <CardCadeia
          titulo="Edital"
          descricao="Edital de Licitação"
          icone={faScroll}
          gradiente="from-purple-500 to-violet-600"
          estado={dfd.cadeia.tem_edital ? 'pronto' : 'pendente'}
          verUrl={dfd.cadeia.tem_edital ? `/app/edital/${dfd.id}` : undefined}
          criarUrl={
            !dfd.cadeia.tem_edital && dfd.cadeia.tem_tr
              ? `/app/edital/novo/${dfd.id}`
              : undefined
          }
        />
      </div>

      <nav
        aria-label="Sumário"
        className="mb-8 rounded-xl border border-slate-200 bg-white p-6 shadow-lg dark:border-slate-700 dark:bg-dark-card"
      >
        <h2 className="mb-4 text-lg font-semibold text-slate-800 dark:text-white">
          <FontAwesomeIcon icon={faListOl} className="mr-2 text-green-600" />
          Sumário
        </h2>
        <div className="grid grid-cols-2 gap-2 md:grid-cols-5">
          {dfd.secoes.map((secao) => (
            <a
              key={secao.chave}
              href={`#secao-${secao.chave}`}
              className="block rounded-lg px-3 py-2 text-sm text-slate-600 transition-colors hover:bg-green-50 hover:text-green-600 dark:text-slate-400 dark:hover:bg-green-900/20 dark:hover:text-green-400"
            >
              {secao.titulo}
            </a>
          ))}
        </div>
      </nav>

      <article className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-lg dark:border-slate-700 dark:bg-dark-card">
        <div className="bg-gradient-to-r from-green-600 to-emerald-600 p-6">
          <h2 className="flex items-center text-2xl font-bold text-white">
            <FontAwesomeIcon icon={faFileAlt} className="mr-3" />
            Documento de Formalização da Demanda (DFD)
          </h2>
          <p className="mt-1 text-green-100">
            Lei 14.133/2021 - Art. 18 | Documento Obrigatório
          </p>
          <p className="mt-3 flex items-center text-sm text-green-100">
            <FontAwesomeIcon icon={faInfoCircle} className="mr-2" />
            5 Seções Principais • Fundamentação Legal • Análise Técnica
          </p>
        </div>

        <div className="space-y-6 p-8">
          <Identificacao dfd={dfd} />
          <CardModalidade dfd={dfd} />

          {dfd.secoes.map((secao) => (
            <section
              key={secao.chave}
              id={`secao-${secao.chave}`}
              className="overflow-hidden rounded-lg border border-slate-200 transition-shadow hover:shadow-md dark:border-slate-700"
            >
              <h3 className="flex items-center border-b border-slate-200 bg-slate-50 px-6 py-4 text-lg font-semibold text-slate-800 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100">
                <FontAwesomeIcon icon={faChevronRight} className="mr-2 text-sm text-green-500" />
                {secao.titulo}
              </h3>
              <div className="p-6">
                {secao.html ? (
                  <div
                    className="conteudo-documento leading-relaxed text-slate-700 dark:text-slate-300"
                    // HTML vindo do backend, convertido pelo mesmo filtro do
                    // Jinja — mesma confiança da tela que ele substitui.
                    dangerouslySetInnerHTML={{ __html: secao.html }}
                  />
                ) : (
                  <p className="text-slate-400">Seção não gerada</p>
                )}
              </div>
            </section>
          ))}

          {dfd.referencias.length > 0 && (
            <section className="overflow-hidden rounded-lg border border-slate-200 dark:border-slate-700">
              <h3 className="flex items-center border-b border-slate-200 bg-slate-50 px-6 py-4 text-lg font-semibold text-slate-800 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100">
                <FontAwesomeIcon icon={faBook} className="mr-2 text-sm text-green-500" />
                Referências
              </h3>
              <ul className="space-y-3 p-6">
                {dfd.referencias.map((ref, i) => (
                  <li key={i} className="text-sm leading-relaxed text-slate-700 dark:text-slate-300">
                    {ref.url ? (
                      <>
                        {ref.texto.replace('acesse aqui', '')}
                        <a
                          href={ref.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-medium text-blue-600 underline hover:text-blue-800"
                        >
                          acesse aqui
                        </a>
                      </>
                    ) : (
                      ref.texto
                    )}
                  </li>
                ))}
              </ul>
            </section>
          )}

          <footer className="mt-8 rounded-lg border-t-2 border-green-600 bg-slate-50 p-6 text-center text-slate-600 dark:bg-slate-800 dark:text-slate-400">
            <p className="mb-3 text-sm">
              <FontAwesomeIcon icon={faCalendarCheck} className="mr-2 text-green-600" />
              <strong>Documento gerado em:</strong>{' '}
              {dfd.data_elaboracao || 'Data não disponível'}
            </p>
            <p className="flex flex-wrap items-center justify-center gap-4 text-xs text-slate-500">
              <span>
                <FontAwesomeIcon icon={faShieldAlt} className="mr-1" />
                Sistema LicitIA.app
              </span>
              <span>•</span>
              <span>
                <FontAwesomeIcon icon={faGavel} className="mr-1" />
                Lei 14.133/2021
              </span>
              <span>•</span>
              <span>
                <FontAwesomeIcon icon={faFileContract} className="mr-1" />
                DFD v2.0
              </span>
            </p>
          </footer>
        </div>
      </article>

      {excluindo && <ModalExcluir dfd={dfd} aoFechar={() => setExcluindo(false)} />}
    </div>
  )
}
